import { z } from "zod";
import type { Any, Override, Without } from "@/shared/types";
import { type ZodObjectWithTelemetry, zodObjectWithTelemetry } from "@/telemetry/helpers/zod";
import type { PluginDefinition } from "../server/types";
import type {
  PluginClientAtomsDefinition,
  PluginClientDefinition,
  PluginClientExtensionDefinition,
  TypedPluginClientBuilder,
} from "./types";

/**
 * ### `definePluginClientConfig()`
 *
 * Standalone version of `definePluginClient().config()` used to break down plugin client definition.
 *
 * Its output must then be passed to `definePluginClient().$config()`.
 *
 * @example
 * ```ts
 * const configDef = definePluginClientConfig({ schema: z.object({ name: z.string() }) });
 * const pluginClient = definePluginClient("my-plugin").$config(configDef); // <-- Here
 *
 * // Equivalent to:
 * const pluginClient = definePluginClient("my-plugin").config({ schema: z.object({ name: z.string() }) }); // <-- Here
 * ```
 * &nbsp;
 *
 * ---
 * @returns The config definition.
 */
export const definePluginClientConfig = <Schema extends z.ZodObject>(
  definition: ZodObjectWithTelemetry<Schema, "input">,
) => zodObjectWithTelemetry(definition);

/**
 * Builder class for the plugin client definition.
 * Do not use directly, use `definePluginClient()` instead.
 */
export class PluginClientBuilder<
  PluginClientDef extends PluginClientDefinition,
  Excluded extends string = never,
> {
  readonly def: PluginClientDef;

  constructor(definition: PluginClientDef) {
    this.def = definition;
  }

  /**
   * ### `.dependencies()`
   *
   * Specify other plugins clients as required by this plugin client.
   *
   * Their config, atoms, and class can then be accessed from `dependencies.*` inside the plugin client's class and atoms.
   *
   * @see TODO: Add docs link
   *
   * @example
   * ```ts
   * const pluginClient = definePluginClient("my-plugin")
   *    .dependencies([anotherPluginClient])
   *    .atoms(({ dependencies }) => {
   *        // Obtain the atoms of the dependency plugin client
   *        const anotherPluginAtoms = dependencies.anotherPlugin.atoms();
   *        // ...
   *    });
   * ```
   * &nbsp;
   *
   * ---
   * @param plugins - The dependencies definitions.
   * @returns TypedPluginClientBuilder
   */
  dependencies<Plugins extends { def: PluginClientDefinition }[]>(plugins: Plugins) {
    const dependencies = plugins.map((p) => p.def);
    const builder = new PluginClientBuilder({ ...this.def, dependencies });
    return builder as TypedPluginClientBuilder<
      Override<
        PluginClientDef,
        "dependencies",
        {
          [K in keyof Plugins]: Without<Plugins[K]["def"], "dependencies">;
        }
      >,
      Excluded | "dependencies"
    >;
  }

  /**
   * ### `.config()`
   *
   * Add a configuration that users can provide to tweak plugin client's behavior.
   *
   * @see TODO: Add docs link
   *
   * @example
   * ```ts
   * const myPluginClient = definePluginClient("my-plugin")
   *    .config({ schema: z.object({ refreshRate: z.number() }) });
   *
   * const myAgentClient = defineAgentClient("my-agent")
   *    .plugins([myPluginClient])
   *    .myPlugin({ refreshRate: 1000 }); // <-- Here
   * ```
   * &nbsp;
   *
   * ---
   *
   * The provided config can then be accessed from `plugin.config` inside atoms and class.
   *
   * @example
   * ```ts
   * const pluginClient = definePluginClient("my-plugin")
   *    .config({ schema: z.object({ refreshRate: z.number() }) });
   *    .atoms(({ config }) => {
   *        const refreshRate = config.refreshRate; // <-- Here
   *    });
   * ```
   * &nbsp;
   *
   * ---
   * @param config - The config definition.
   * @returns TypedPluginClientBuilder
   */
  config<Schema extends z.ZodObject>(config: ZodObjectWithTelemetry<Schema, "input">) {
    return this.$config(zodObjectWithTelemetry(config));
  }

  /**
   * ### `.$config()`
   *
   * Define plugin client config from the output of `definePluginClientConfig()`.
   *
   * @see TODO: Add docs link for `definePluginClientConfig()`
   *
   * ---
   * @param config - The config definition.
   * @returns TypedPluginClientBuilder
   */
  $config<ConfigDef extends ZodObjectWithTelemetry<z.ZodObject, "output">>(config: ConfigDef) {
    const builder = new PluginClientBuilder({ ...this.def, config });
    return builder as TypedPluginClientBuilder<
      Override<PluginClientDef, "config", ConfigDef>,
      Excluded | "config" | "$config"
    >;
  }

  /**
   * ### `.class()`
   *
   * TODO
   *
   * @see TODO: Add docs link
   *
   * ---
   * @param input - The class definition.
   * @returns TypedPluginClientBuilder
   */
  class<Extension extends PluginClientExtensionDefinition<PluginClientDef, false>>(
    extension: Extension,
  ) {
    const builder = new PluginClientBuilder({ ...this.def, class: extension });
    return builder as TypedPluginClientBuilder<
      Override<PluginClientDef, "class", Extension>,
      Excluded | "class"
    >;
  }

  /**
   * ### `.atoms()`
   *
   * Add reactive states (atoms) that can then be consumed from various UI frameworks
   * (React, Vue, Svelte, etc.) to render the plugin data in real-time on the user interface.
   *
   * Atoms are powered by [nanostores](https://github.com/nanostores/nanostores).
   *
   * @see TODO: Add docs link
   *
   * @example
   * ```ts
   * import { onMount } from "nanostores";
   *
   * const pluginClient = definePluginClient("my-plugin")
   *    .atoms(({ plugin }) => {
   *        const value = atom(0);
   *        onMount(() => {
   *            setInterval(async () => {
   *                const context = await plugin.context.get();
   *                value.set(context.value + 1);
   *            }, 1000);
   *        });
   *        return { value };
   *    });
   * ```
   * &nbsp;
   *
   * ---
   *
   * Then from a React component, you can render the `value` atom:
   * ```ts
   * import { useStore } from "nanostores/react";
   *
   * export default function Page() {
   *    const agent = useAgent("my-agent");
   *    const value = useStore(agent.myPlugin.atoms.value);
   *    return <div>{value}</div>;
   * };
   * ```
   * &nbsp;
   *
   * ---
   * @param definition - The atoms definition.
   * @returns TypedPluginClientBuilder
   */
  atoms<
    const Names extends string[],
    Atoms extends PluginClientAtomsDefinition<PluginClientDef, false, Names>,
  >(atoms: Atoms) {
    const builder = new PluginClientBuilder({ ...this.def, atoms });
    return builder as TypedPluginClientBuilder<
      Override<PluginClientDef, "atoms", Atoms>,
      Excluded | "atoms"
    >;
  }
}

type ServerPluginShape = { def: PluginDefinition };

/**
 * ### `definePluginClient()`
 *
 * Define a new Life.js plugin client.
 *
 * @see TODO: Add docs link
 */
export function definePluginClient<
  ServerPlugin extends ServerPluginShape | undefined = undefined,
  const Name extends string = ServerPlugin extends ServerPluginShape
    ? ServerPlugin["def"]["name"]
    : string,
>(name: Name) {
  // Extract the server definition type precisely if provided, else default to a broad type
  type ExtractServerDefinition = Without<
    ServerPlugin extends ServerPluginShape ? ServerPlugin["def"] : PluginDefinition,
    "dependencies" // remove dependencies (unused) to reduce type explosion
  > & { dependencies: [] };

  // Create a default definition for the plugin client
  const defaultDefinition = {
    name,
    config: zodObjectWithTelemetry({ schema: z.object() }),
    dependencies: [],
    class: <const _ProvidedConfigs>(..._args: Any[]) => class {},
    atoms: <const _ProvidedConfigs>(..._args: Any[]) => [],
    $serverDef: null as unknown as ExtractServerDefinition,
  } as const satisfies PluginClientDefinition;

  // Return the builder with the extracted server definition type
  return new PluginClientBuilder<typeof defaultDefinition>(defaultDefinition);
}
